import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import SectionHeading from './ui/SectionHeading';
import { RevealGroup, RevealItem } from './ui/Reveal';
import { caseStudies, type CaseStudy } from '../lib/caseStudies';
import { routeHref } from '../lib/router';

const CaseCard: React.FC<{ item: CaseStudy }> = ({ item }) => (
    <RevealItem className={item.large ? 'md:col-span-2' : ''}>
        <a
            href={routeHref(`/work/${item.slug}`)}
            aria-label={`Read the ${item.title} case study`}
            className="group relative flex flex-col h-full overflow-hidden rounded-2xl card-surface hover:border-white/[0.14] transition-all duration-300"
        >
            <div className={`relative overflow-hidden ${item.large ? 'h-64' : 'h-48'}`}>
                <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent" />

                {/* metric badge */}
                <div className="absolute top-4 left-4 rounded-xl glass px-3 py-2">
                    <div className="font-display text-xl font-bold text-white leading-none">{item.metric.value}</div>
                    <div className="text-[11px] text-text-muted mt-1">{item.metric.label}</div>
                </div>

                {/* hover arrow */}
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white text-navy-900 grid place-items-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                    <ArrowUpRight className="w-4 h-4" />
                </div>
            </div>

            <div className="flex flex-col flex-1 p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags.map((tag) => (
                        <span key={tag} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/[0.04] text-text-muted border border-white/[0.06]">
                            {tag}
                        </span>
                    ))}
                </div>
                <h3 className="font-display text-lg font-bold text-white group-hover:text-brand-cyan transition-colors">
                    {item.title}
                </h3>
                <p className="mt-2 text-sm text-text-muted leading-relaxed">{item.description}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-cyan opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    Read the case study
                    <ArrowUpRight className="w-4 h-4" />
                </span>
            </div>
        </a>
    </RevealItem>
);

const CaseStudies: React.FC = () => {
    return (
        <section id="work" className="relative py-24 md:py-32">
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <SectionHeading
                        align="left"
                        eyebrow="Selected work"
                        title={<>Automations that pay for <span className="text-gradient">themselves</span></>}
                        subtitle="A snapshot of systems we've shipped — each one quietly saving its owner hours, headcount, and revenue every single day. Open any project for the full build."
                    />
                    <a href="#contact" className="hidden md:inline-flex items-center gap-2 text-brand-cyan font-semibold hover:gap-3 transition-all shrink-0">
                        Start your project <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>

                <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.06}>
                    {caseStudies.map((item) => (
                        <CaseCard key={item.slug} item={item} />
                    ))}
                </RevealGroup>
            </div>
        </section>
    );
};

export default CaseStudies;
